import { Button } from '@/lib/Buttons';
import { DataSourceIcon } from '@/lib';
import { DataSourceIDEnum } from '@/models/dataSource';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { SourceConnectorCatalogItem } from '../sourceConnectors/useSourceConnectorCatalogItems';

type SourceConnectorCatalogCardProps = {
  item: SourceConnectorCatalogItem;
  onConnect: () => void;
  onManage: () => void;
  className?: string;
};

export const SourceConnectorCatalogCard: FC<SourceConnectorCatalogCardProps> = ({
  item,
  onConnect,
  onManage,
  className,
}) => {
  const { t } = useTranslation('data_sources');
  const { dataSourceType, isAuthenticated, isPlaceholder } = item;
  const typeId = dataSourceType.id as DataSourceIDEnum;

  return (
    <div
      className={twMerge(
        'flex w-full flex-col gap-4 rounded-xl border border-quaternary-300 bg-white-100 p-4 shadow',
        className
      )}
    >
      <div className='flex flex-wrap items-center gap-3 lg:flex-nowrap'>
        <DataSourceIcon type={typeId} />
        <h3 className='text-base font-semibold uppercase tracking-wide text-gray-700'>
          {t(`catalog.${typeId}.title`, { defaultValue: dataSourceType.name })}
        </h3>
      </div>

      {isPlaceholder && (
        <p className='text-xs text-amber-700'>{t('source_connectors.placeholder_notice')}</p>
      )}

      <div className='flex items-center justify-between gap-3'>
        <span
          className={twMerge(
            'inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium',
            isAuthenticated ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
          )}
        >
          <span className={twMerge('size-2 rounded-full', isAuthenticated ? 'bg-green-500' : 'bg-gray-400')} />
          {isAuthenticated ? t('source_connectors.connected_status') : t('source_connectors.not_connected')}
        </span>

        {isAuthenticated ? (
          <button
            type='button'
            className='text-sm font-semibold text-brand-600 hover:underline'
            onClick={(e) => {
              e.stopPropagation();
              onManage();
            }}
          >
            {t('source_connectors.manage')}
          </button>
        ) : (
          <Button
            type='button'
            className='h-9 px-4'
            disabled={isPlaceholder}
            onClick={(e) => {
              e.stopPropagation();
              onConnect();
            }}
          >
            {t('source_connectors.connect')}
          </Button>
        )}
      </div>
    </div>
  );
};
